import { useCart } from 'components/Cart';
import { isCarisDetect } from 'components/CheckoutML/CheckoutML.utils';
import { isProductInOrder } from 'components/CheckoutQuest/CheckoutQuest.utils';
import PrePurchaseScreener from 'components/PrePurchaseScreener';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  formatEligibilityHash,
  useContentfulScreenerByMetadataId,
} from 'utils/hooks/useContentfulScreenerByMetadataId';
import { useProductVariantId } from 'utils/hooks/useProductVariantId';

export type EligibilityScreenerModalProps = {
  productSlug: string;
  screenerMetadataId: string;
};

const removeHash = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.history.replaceState(
    null,
    '',
    window.location.pathname + window.location.search,
  );
};

const EligibilityScreenerModal = ({
  productSlug,
  screenerMetadataId,
}: EligibilityScreenerModalProps) => {
  const { addItemToCartAsync, order } = useCart();
  const variantId = useProductVariantId(productSlug);
  const screenerResult = useContentfulScreenerByMetadataId(screenerMetadataId);
  const screenerData = screenerResult?.screenerData;
  const orderTestHash = formatEligibilityHash(
    screenerResult?.orderTestHash ?? '',
  );
  const [isOpen, setIsOpen] = useState(false);
  const isAddingToCartRef = useRef(false);
  // Path 2 (partner #order-test bounce-back): only Caris Detect must hard-block
  // re-open / re-add. Other eligibility products may allow qty > 1 via this deep-link.
  const alreadyInCart =
    isCarisDetect(productSlug) &&
    !!order &&
    isProductInOrder(order, [productSlug]);

  const closeModal = useCallback(() => {
    removeHash();
    setIsOpen(false);
  }, []);

  const matchesEligibilityHash = useCallback(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.location.hash === orderTestHash;
  }, [orderTestHash]);

  useEffect(() => {
    if (!screenerData || !variantId) {
      return;
    }

    // Partner `#order-test` deep-link bypasses the MDP "Added to cart" CTA.
    // If the SKU is already in cart, do not reopen the screener / re-add path.
    if (alreadyInCart) {
      if (matchesEligibilityHash()) {
        removeHash();
      }
      setIsOpen(false);
      return;
    }

    if (matchesEligibilityHash()) {
      setIsOpen(true);
    }

    const handleHashChange = () => {
      if (alreadyInCart) {
        removeHash();
        setIsOpen(false);
        return;
      }
      setIsOpen(matchesEligibilityHash());
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [alreadyInCart, matchesEligibilityHash, screenerData, variantId]);

  const handleEligible = async () => {
    if (!variantId || alreadyInCart) {
      closeModal();
      return;
    }

    // Repeated clicks while the request is in flight would each add a line item
    if (isAddingToCartRef.current) {
      return;
    }

    isAddingToCartRef.current = true;

    try {
      const result = await addItemToCartAsync({
        variant_id: variantId,
        quantity: 1,
      });

      if (!result) {
        return;
      }

      sessionStorage.setItem(`${productSlug}_eligible`, 'true');

      closeModal();
    } finally {
      isAddingToCartRef.current = false;
    }
  };

  if (!screenerData || !variantId) {
    return null;
  }

  return (
    <PrePurchaseScreener
      isV2
      isEligibility
      screenerData={screenerData}
      modalOpen={isOpen}
      modalOpenCallback={closeModal}
      eligibleOptionCallback={handleEligible}
      ineligibleOptionCallback={closeModal}
    />
  );
};

export default EligibilityScreenerModal;
